import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  listJobs,
  getJobStats,
  updateJobStatus,
  triggerJob,
  cancelJob,
  deleteJob,
  deleteJobsBulk,
  type Job,
  type JobStats,
  type JobStatus,
} from '../api/client';
import { Briefcase, ChevronDown, ChevronRight, Info, Pause, Play, Trash2 } from 'lucide-react';
import { useError } from '../context/ErrorContext';
import { panelPath } from '../config/paths';
import ConfirmDialog from './ConfirmDialog';
import { SkeletonBar, SkeletonTableRow } from './Skeleton';

const JOB_TYPE_KEYS: Record<string, string> = {
  screens_upload: 'panel.jobTypeScreensUpload',
  screens_ai_translation: 'panel.jobTypeScreensAITranslation',
  metadata_export: 'panel.jobTypeMetadataExport',
  metadata_translation: 'panel.jobTypeMetadataTranslation',
  metadata_upload: 'panel.jobTypeMetadataUpload',
  ai_translation: 'panel.jobTypeAITranslation',
  publish: 'panel.jobTypePublish',
  page_site_deploy: 'panel.jobTypePageSiteDeploy',
  revenuecat_ai_translation: 'panel.jobTypeRevenueCatAITranslation',
  revenuecat_upload: 'panel.jobTypeRevenueCatUpload',
  revenuecat_product_sync: 'panel.jobTypeRevenueCatProductSync',
  revenuecat_full_setup: 'panel.jobTypeRevenueCatFullSetup',
  iap_pricing_upload: 'panel.jobTypeIAPPricingUpload',
};

const STATUS_KEYS: Record<JobStatus, string> = {
  pending: 'panel.statusPending',
  processing: 'panel.statusProcessing',
  paused: 'panel.statusPaused',
  completed: 'panel.statusCompleted',
  failed: 'panel.statusFailed',
};

const STATUS_COLORS: Record<JobStatus, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  paused: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

/** Job list status filter (multi-select). Empty set = show all. */
const JOB_STATUS_FILTERS: { value: JobStatus; labelKey: string; activeClass: string }[] = [
  {
    value: 'processing',
    labelKey: 'panel.statusProcessing',
    activeClass: 'bg-blue-100 text-blue-800 ring-1 ring-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:ring-blue-700',
  },
  {
    value: 'pending',
    labelKey: 'panel.statusPending',
    activeClass: 'bg-amber-100 text-amber-800 ring-1 ring-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-700',
  },
  {
    value: 'paused',
    labelKey: 'panel.statusPaused',
    activeClass: 'bg-gray-200 text-gray-800 ring-1 ring-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:ring-gray-500',
  },
  {
    value: 'completed',
    labelKey: 'panel.statusCompleted',
    activeClass: 'bg-green-100 text-green-800 ring-1 ring-green-300 dark:bg-green-900/40 dark:text-green-300 dark:ring-green-700',
  },
  {
    value: 'failed',
    labelKey: 'panel.statusFailed',
    activeClass: 'bg-red-100 text-red-800 ring-1 ring-red-300 dark:bg-red-900/40 dark:text-red-300 dark:ring-red-700',
  },
];

function matchesJobStatusFilter(status: JobStatus, filters: Set<JobStatus>): boolean {
  if (filters.size === 0) return true;
  return filters.has(status);
}

type JobGroup = {
  parent: Job;
  children: Job[];
};

function groupJobs(jobs: Job[]): JobGroup[] {
  const byParent = new Map<string, Job[]>();
  for (const job of jobs) {
    const parentId = (job.parentJobId ?? '').trim();
    if (!parentId) continue;
    const list = byParent.get(parentId) ?? [];
    list.push(job);
    byParent.set(parentId, list);
  }
  byParent.forEach(list => {
    list.sort((a: Job, b: Job) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  });

  const roots = jobs.filter(j => !(j.parentJobId ?? '').trim());
  const rootIds = new Set(roots.map(j => j.id));

  // Parent listede yoksa (status filtresi vb.) orphan alt job'ları tek başına göster.
  const orphanGroups: JobGroup[] = [];
  byParent.forEach((children, parentId) => {
    if (!rootIds.has(parentId)) {
      for (const child of children) {
        orphanGroups.push({ parent: child, children: [] });
      }
    }
  });

  const groups: JobGroup[] = roots.map(parent => ({
    parent,
    children: byParent.get(parent.id) ?? [],
  }));
  return [...groups, ...orphanGroups];
}

const Jobs: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const jobTypeLabel = (type: string) => t(JOB_TYPE_KEYS[type] ?? type);
  const statusLabel = (s: JobStatus) => t(STATUS_KEYS[s]);

  const [jobs, setJobs] = useState<Job[]>([]);
  const [stats, setStats] = useState<JobStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilters, setStatusFilters] = useState<Set<JobStatus>>(() => new Set());
  const [actingId, setActingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [deleteConfirmJob, setDeleteConfirmJob] = useState<Job | null>(null);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const { showError: showErrorPopup } = useError();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Tüm job'ları çek; status filtresini üst seviye job'larda uygula (alt job'lar grup altında kalsın).
      const [jobsRes, statsRes] = await Promise.all([listJobs(), getJobStats()]);
      const sorted = [...jobsRes].sort((a, b) => {
        const finished = (s: JobStatus) => s === 'completed' || s === 'failed';
        const aDone = finished(a.status);
        const bDone = finished(b.status);
        if (aDone !== bDone) return aDone ? 1 : -1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      setJobs(sorted);
      setStats(statsRes);
      setExpandedIds(prev => {
        const next = new Set<string>();
        for (const j of sorted) {
          if (j.type === 'publish' && (j.status === 'processing' || j.status === 'pending')) {
            next.add(j.id);
          } else if (prev.has(j.id)) {
            next.add(j.id);
          }
        }
        return next;
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('panel.loadFailed');
      showErrorPopup(msg);
    } finally {
      setLoading(false);
    }
  }, [showErrorPopup, t]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const all = groupJobs(jobs);
    if (statusFilters.size === 0) return all;
    return all.filter(g => matchesJobStatusFilter(g.parent.status, statusFilters));
  }, [jobs, statusFilters]);
  const topLevelJobs = useMemo(() => groups.map(g => g.parent), [groups]);
  const hasActiveTopLevel = useMemo(
    () => topLevelJobs.some(j => j.status === 'pending' || j.status === 'processing'),
    [topLevelJobs]
  );

  const toggleStatusFilter = (value: JobStatus) => {
    setStatusFilters(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  // Aktif top-level job'larda (özellikle publish) alt job'lar oluştukça listeyi güncelle.
  useEffect(() => {
    if (!hasActiveTopLevel) return;
    const id = window.setInterval(() => {
      void listJobs()
        .then(jobsRes => {
          const sorted = [...jobsRes].sort((a, b) => {
            const finished = (s: JobStatus) => s === 'completed' || s === 'failed';
            const aDone = finished(a.status);
            const bDone = finished(b.status);
            if (aDone !== bDone) return aDone ? 1 : -1;
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          });
          setJobs(sorted);
          setExpandedIds(prev => {
            const next = new Set(prev);
            for (const j of sorted) {
              if (j.type === 'publish' && (j.status === 'processing' || j.status === 'pending')) {
                next.add(j.id);
              }
            }
            return next;
          });
        })
        .catch(() => {});
    }, 2000);
    return () => window.clearInterval(id);
  }, [hasActiveTopLevel]);

  const handlePause = async (job: Job) => {
    if (job.status !== 'processing') return;
    setActingId(job.id);
    try {
      await updateJobStatus(job.id, { status: 'paused' });
      try {
        await cancelJob(job.id);
      } catch {
        // Status already paused; watcher will cancel if worker call fails.
      }
      await load();
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setActingId(null);
    }
  };

  const handleStart = async (job: Job) => {
    const canRun =
      job.status === 'paused' ||
      job.status === 'pending' ||
      job.status === 'completed' ||
      job.status === 'failed';
    if (!canRun) return;
    setActingId(job.id);
    try {
      await triggerJob(job.id);
      await load();
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setActingId(null);
    }
  };

  const handleDelete = (job: Job) => {
    setDeleteConfirmJob(job);
  };

  const confirmDeleteJob = async () => {
    if (!deleteConfirmJob) return;
    setActingId(deleteConfirmJob.id);
    try {
      await deleteJob(deleteConfirmJob.id);
      setDeleteConfirmJob(null);
      await load();
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setActingId(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === topLevelJobs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(topLevelJobs.map(j => j.id)));
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setBulkDeleteConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const { failedIds } = await deleteJobsBulk(Array.from(selectedIds));
      setSelectedIds(new Set());
      setBulkDeleteConfirmOpen(false);
      await load();
      if (failedIds.length > 0) {
        showErrorPopup(`${failedIds.length} job silinemedi.`);
      }
    } catch (e) {
      showErrorPopup(e);
    } finally {
      setBulkDeleting(false);
    }
  };

  const renderJobRow = (job: Job, opts: { nested?: boolean; childCount?: number; expanded?: boolean }) => {
    const nested = Boolean(opts.nested);
    const childCount = opts.childCount ?? 0;
    const hasChildren = childCount > 0;
    const expanded = Boolean(opts.expanded);
    return (
      <tr
        key={job.id}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest('button') || target.closest('input') || target.closest('a')) return;
          navigate(panelPath(`/jobs/${job.id}`));
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          const target = e.target as HTMLElement;
          if (target.closest('button') || target.closest('input') || target.closest('a')) return;
          e.preventDefault();
          navigate(panelPath(`/jobs/${job.id}`));
        }}
        className={`border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 cursor-pointer ${
          nested ? 'bg-gray-50/40 dark:bg-gray-900/20' : ''
        }`}
      >
        <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
          {!nested && (
            <input
              type="checkbox"
              checked={selectedIds.has(job.id)}
              onChange={() => toggleSelect(job.id)}
              className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              aria-label={t('panel.selectJob')}
            />
          )}
        </td>
        <td className="py-3 px-4">
          <div className={`flex items-center gap-1.5 ${nested ? 'pl-6' : ''}`}>
            {hasChildren ? (
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  toggleExpand(job.id);
                }}
                className="p-0.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label={expanded ? t('panel.collapse') : t('panel.expand')}
                title={expanded ? t('panel.collapse') : t('panel.expand')}
              >
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            ) : (
              nested ? <span className="w-5" /> : null
            )}
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
              <Briefcase className="h-3.5 w-3.5" />
              {jobTypeLabel(job.type)}
              {hasChildren ? (
                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                  ({childCount})
                </span>
              ) : null}
            </span>
          </div>
        </td>
        <td className="py-3 px-4">
          <span
            className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[job.status]}`}
          >
            {statusLabel(job.status)}
          </span>
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-[64px] h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
              <div
                className="h-full bg-primary-500 dark:bg-primary-400 rounded-full transition-all"
                style={{ width: `${job.progress ?? 0}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 w-8">
              {job.progress ?? 0}%
            </span>
          </div>
        </td>
        <td className="py-3 px-4 text-gray-500 dark:text-gray-400">
          {new Date(job.createdAt).toLocaleString('tr-TR')}
        </td>
        <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
          {!nested && job.status === 'processing' && (
            <button
              type="button"
              onClick={() => handlePause(job)}
              disabled={actingId === job.id}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-800/40 disabled:opacity-50"
              title={t('panel.pause')}
            >
              <Pause className="h-3.5 w-3.5" />
              {t('panel.pause')}
            </button>
          )}
          {!nested &&
            (job.status === 'paused' ||
              job.status === 'pending' ||
              job.status === 'completed' ||
              job.status === 'failed') && (
            <button
              type="button"
              onClick={() => handleStart(job)}
              disabled={actingId === job.id}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-800/40 disabled:opacity-50"
              title={job.status === 'completed' || job.status === 'failed' ? t('panel.restart') : t('panel.start')}
            >
              <Play className="h-3.5 w-3.5" />
              {job.status === 'completed' || job.status === 'failed'
                ? t('panel.restart')
                : t('panel.start')}
            </button>
          )}
        </td>
        <td className="py-3 px-4" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            {!nested && (
              <button
                type="button"
                onClick={() => handleDelete(job)}
                disabled={actingId === job.id}
                className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20 disabled:opacity-50"
                title={t('common.delete')}
                aria-label={t('common.delete')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <Link
              to={panelPath(`/jobs/${job.id}`)}
              className="p-1.5 rounded inline-flex items-center text-primary-600 dark:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              title={t('common.detail')}
            >
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="p-4 space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">
          {t('panel.jobsTitle')}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
          {t('panel.jobsSubtitle')}
        </p>
      </div>

      <div className="rounded-xl border border-primary-200 dark:border-primary-800/60 bg-primary-50/40 dark:bg-primary-900/10 p-4">
        <div className="flex items-center gap-2 mb-1">
          <Info className="h-4 w-4 text-primary-600 dark:text-primary-400 shrink-0" />
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            {t('onboarding.jobs.infoTitle')}
          </h3>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('onboarding.jobs.infoBody')}
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total}
            </div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-0.5">
              {t('panel.totalJobs')}
            </div>
          </div>
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/10 p-4">
            <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">
              {stats.pending}
            </div>
            <div className="text-xs font-medium text-amber-600 dark:text-amber-500 uppercase tracking-wider mt-0.5">
              {t('panel.statusPending')}
            </div>
          </div>
          <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-4">
            <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
              {stats.processing}
            </div>
            <div className="text-xs font-medium text-blue-600 dark:text-blue-500 uppercase tracking-wider mt-0.5">
              {t('panel.statusProcessing')}
            </div>
          </div>
          <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10 p-4">
            <div className="text-2xl font-bold text-green-700 dark:text-green-400">
              {stats.completed}
            </div>
            <div className="text-xs font-medium text-green-600 dark:text-green-500 uppercase tracking-wider mt-0.5">
              {t('panel.statusCompleted')}
            </div>
          </div>
          <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 p-4">
            <div className="text-2xl font-bold text-red-700 dark:text-red-400">
              {stats.failed}
            </div>
            <div className="text-xs font-medium text-red-600 dark:text-red-500 uppercase tracking-wider mt-0.5">
              {t('panel.statusFailed')}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50 p-4">
            <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
              {stats.paused}
            </div>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-0.5">
              {t('panel.statusPaused')}
            </div>
          </div>
        </div>
      )}
      {loading && !stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              <SkeletonBar className="h-8 w-12 mb-2" />
              <SkeletonBar width="70%" className="h-3" />
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('panel.jobList')}
            </h2>
            {selectedIds.size > 0 && (
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50"
                title={t('panel.deleteSelected')}
              >
                <Trash2 className="h-4 w-4" />
                {bulkDeleting ? t('panel.deleting') : t('panel.deleteSelectedCount', { count: selectedIds.size })}
              </button>
            )}
          </div>
          <div
            className="flex flex-wrap items-center gap-1.5"
            role="group"
            aria-label={t('panel.jobDetailSubStatusFilter')}
          >
            {JOB_STATUS_FILTERS.map(({ value, labelKey, activeClass }) => {
              const active = statusFilters.has(value);
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleStatusFilter(value)}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                    active
                      ? activeClass
                      : 'bg-gray-50 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
                  }`}
                >
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                  <th className="py-3 px-4 w-10">
                    <span className="sr-only">{t('panel.selectAll')}</span>
                  </th>
                  <th className="py-3 px-4 font-semibold">{t('panel.type')}</th>
                  <th className="py-3 px-4 font-semibold">{t('panel.status')}</th>
                  <th className="py-3 px-4 font-semibold w-32">{t('panel.progress')}</th>
                  <th className="py-3 px-4 font-semibold">{t('panel.createdAt')}</th>
                  <th className="py-3 px-4 font-semibold w-28">{t('panel.actions')}</th>
                  <th className="py-3 px-4 font-semibold w-10"></th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonTableRow key={i} cols={7} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                  <th className="py-3 px-4 w-10">
                    <input
                      type="checkbox"
                      checked={topLevelJobs.length > 0 && selectedIds.size === topLevelJobs.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                      aria-label={t('panel.selectAll')}
                    />
                  </th>
                  <th className="py-3 px-4 font-semibold">{t('panel.type')}</th>
                  <th className="py-3 px-4 font-semibold">{t('panel.status')}</th>
                  <th className="py-3 px-4 font-semibold w-32">{t('panel.progress')}</th>
                  <th className="py-3 px-4 font-semibold">{t('panel.createdAt')}</th>
                  <th className="py-3 px-4 font-semibold w-28">{t('panel.actions')}</th>
                  <th className="py-3 px-4 font-semibold w-10"></th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="py-8 text-center text-gray-500 dark:text-gray-400"
                    >
                      {statusFilters.size > 0
                        ? t('panel.jobDetailSubStatusFilterEmpty')
                        : t('panel.noJobs')}
                    </td>
                  </tr>
                ) : (
                  groups.flatMap(({ parent, children }) => {
                    const expanded = expandedIds.has(parent.id);
                    const rows = [
                      renderJobRow(parent, { childCount: children.length, expanded }),
                    ];
                    if (children.length > 0 && expanded) {
                      for (const child of children) {
                        rows.push(renderJobRow(child, { nested: true }));
                      }
                    }
                    return rows;
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={Boolean(deleteConfirmJob)}
        onClose={() => {
          if (actingId !== deleteConfirmJob?.id) setDeleteConfirmJob(null);
        }}
        title={t('panel.deleteJobTitle')}
        message={
          deleteConfirmJob
            ? t('panel.confirmDeleteJob', { type: jobTypeLabel(deleteConfirmJob.type) })
            : ''
        }
        confirmLabel={actingId === deleteConfirmJob?.id ? t('panel.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDeleteJob()}
        confirmLoading={actingId === deleteConfirmJob?.id}
        closeDisabled={actingId === deleteConfirmJob?.id}
        variant="danger"
      />
      <ConfirmDialog
        open={bulkDeleteConfirmOpen}
        onClose={() => {
          if (!bulkDeleting) setBulkDeleteConfirmOpen(false);
        }}
        title={t('panel.deleteJobsTitle')}
        message={t('panel.confirmDeleteJobs', { count: selectedIds.size })}
        confirmLabel={bulkDeleting ? t('panel.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmBulkDelete()}
        confirmLoading={bulkDeleting}
        closeDisabled={bulkDeleting}
        variant="danger"
      />
    </div>
  );
};

export default Jobs;
